import React from 'react';
import { Link } from 'react-router-dom';
import { FaChessPawn, FaChessKnight, FaChessRook, FaChessKing, FaChessQueen } from 'react-icons/fa';
import { Helmet } from "react-helmet";
import { Button } from './components/ui/button';
import './HomePage.css';

function HomePage() {
  return (
    <div className="home-container">
      <Helmet>
        <title>Online Chess Puzzle Training | Puzzlify</title>
        <meta
          name="description"
          content="Train chess tactics with the Woodpecker Method, adaptive puzzles, and custom puzzles generated from your own games on Puzzlify."
        />
        <link
          rel="canonical"
          href="https://puzzlify.io/"
        />
      </Helmet>
      <header className="bg-[#2f8cbb] px-4 py-7 text-center text-white sm:py-9">
        <div className="relative inline-block" aria-label="Puzzlify">
          <FaChessQueen
            className="absolute right-full top-[38%] mr-3 size-12 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-[#fff7d6] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.32),0_14px_30px_rgba(16,57,78,0.22)] sm:mr-4 sm:size-16 sm:p-3.5"
            aria-hidden="true"
          />
          <h1 className="text-4xl font-bold tracking-wide sm:text-5xl">Puzzlify</h1>
        </div>
        <p className="mt-2 text-sm text-white/90 sm:text-base">A Chess Puzzle Training Platform</p>
      </header>

      <main className="features-grid w-full">
        <Link to="/woodpecker" className="feature-card woodpecker group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-700 focus-visible:ring-offset-4" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="feature-icon">
            <FaChessKnight />
          </div>
          <h2>The Woodpecker Method</h2>
          <p>Train with the Woodpecker Method Online. Master tactical patterns through systematic repetition. Perfect for players looking to build strong tactical foundations.</p>
          <ul className="feature-benefits !mt-auto !pt-6">
            <li><FaChessPawn /> Structured learning cycles</li>
            <li><FaChessPawn /> Progress tracking</li>
            <li><FaChessPawn /> Adaptive difficulty</li>
          </ul>
          <Button asChild variant="brand" size="action" className="mt-0 w-full group-hover:bg-[#1b658a]"><span>Start Training</span></Button>
        </Link>

        <Link to="/create" className="feature-card create-puzzles group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-700 focus-visible:ring-offset-4" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="feature-icon">
            <FaChessRook />
          </div>
          <h2>Create Puzzles from Your Games</h2>
          <p>Generate personalized puzzles from your own games. Learn from your mistakes and improve your play.</p>
          <ul className="feature-benefits !mt-auto !pt-6">
            <li><FaChessPawn /> Custom puzzle sets</li>
            <li><FaChessPawn /> Learn from your games</li>
            <li><FaChessPawn /> Flexible training</li>
          </ul>
          <Button asChild variant="brand" size="action" className="mt-0 w-full group-hover:bg-[#1b658a]"><span>Create Puzzles</span></Button>
        </Link>

        <Link to="/freeplay" className="feature-card group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-700 focus-visible:ring-offset-4" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="feature-icon">
            <FaChessKing />
          </div>
          <h2>Adaptive Puzzle Mode</h2>
          <p>Jump into a personalized puzzle stream without signing in. The difficulty adapts as you solve.</p>
          <ul className="feature-benefits !mt-auto !pt-6">
            <li><FaChessPawn /> Instant access</li>
            <li><FaChessPawn /> Rating-aware selection</li>
            <li><FaChessPawn /> Progression feedback</li>
          </ul>
          <Button asChild variant="brand" size="action" className="mt-0 w-full group-hover:bg-[#1b658a]"><span>Play Adaptive Puzzles</span></Button>
        </Link>

        <article className="feature-card openings" aria-labelledby="openings-heading">
          <div className="feature-icon" aria-hidden="true">
            <FaChessPawn />
          </div>
          <h2 id="openings-heading">Openings</h2>
          <p>Understand opening ideas, then build recall with repeatable sequence training.</p>
          <ul className="openings-options">
            <li>
              <strong>Learn openings</strong>
              <p>Understand the moves, ideas, and variations.</p>
              <Button asChild variant="brand" size="action" className="mt-2 w-full"><Link to="/openings/learn">Start learning openings</Link></Button>
            </li>
            <li>
              <strong>Train openings</strong>
              <p>Remember opening lines through repeated cycles.</p>
              <Button asChild variant="brand" size="action" className="mt-2 w-full"><Link to="/openings">Start opening training</Link></Button>
            </li>
          </ul>
        </article>
      </main>

      <footer className="home-footer">
        <p>© 2025 Puzzlify. Operated by Mountainsoft LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default HomePage;
